import { API_CONFIG, getApiConfig, ChatResponse, ChatCreate, ChatListResponse, MessageResponse, MessageCreate, MessageListResponse, ChatUpdateRequest } from '../config/api';

class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // Use the enhanced API configuration that supports VPC endpoints
    const config = getApiConfig();
    this.baseUrl = config.BASE_URL;
  }

  // Set authentication token
  setToken(token: string) {
    this.token = token;
  }

  // Get authentication headers
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Generic request method
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return {} as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication endpoints
  async login(): Promise<any> {
    return this.request(API_CONFIG.ENDPOINTS.LOGIN);
  }

  async callback(code: string): Promise<any> {
    return this.request(`${API_CONFIG.ENDPOINTS.CALLBACK}?code=${code}`);
  }

  async getCurrentUser(): Promise<any> {
    return this.request(API_CONFIG.ENDPOINTS.ME);
  }

  // Chat management endpoints
  async createChat(userId: string, chatData: ChatCreate): Promise<ChatResponse> {
    return this.request(API_CONFIG.ENDPOINTS.CREATE_CHAT(userId), {
      method: 'POST',
      body: JSON.stringify(chatData),
    });
  }

  async getUserChats(userId: string, limit: number = 50, offset: number = 0): Promise<ChatListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    return this.request(`${API_CONFIG.ENDPOINTS.GET_USER_CHATS(userId)}?${params}`);
  }

  async getChat(chatId: string): Promise<ChatResponse> {
    return this.request(API_CONFIG.ENDPOINTS.GET_CHAT(chatId));
  }

  async updateChat(chatId: string, updateData: ChatUpdateRequest): Promise<ChatResponse> {
    return this.request(API_CONFIG.ENDPOINTS.UPDATE_CHAT(chatId), {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteChat(chatId: string): Promise<void> {
    return this.request(API_CONFIG.ENDPOINTS.DELETE_CHAT(chatId), {
      method: 'DELETE',
    });
  }

  async getChatMessages(chatId: string, limit: number = 100, offset: number = 0): Promise<MessageListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    return this.request(`${API_CONFIG.ENDPOINTS.GET_CHAT_MESSAGES(chatId)}?${params}`);
  }

  async addMessage(chatId: string, messageData: MessageCreate): Promise<MessageResponse> {
    return this.request(API_CONFIG.ENDPOINTS.ADD_MESSAGE(chatId), {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // Documentation endpoints
  async getWebSocketDocs(): Promise<any> {
    return this.request(API_CONFIG.ENDPOINTS.WEBSOCKET_DOCS);
  }

  async getApiOverview(): Promise<any> {
    return this.request(API_CONFIG.ENDPOINTS.API_OVERVIEW);
  }

  async healthCheck(): Promise<any> {
    return this.request(API_CONFIG.ENDPOINTS.HEALTH_CHECK);
  }
}

// Export singleton instance
export const apiService = new ApiService(); 